/**
 * @file SectionCard.tsx
 * @description Блок сводки: карточка с заголовком секции и ссылкой «Все … ›»
 * (phase19.31, шаг О4; uiux-design.md §5 «Сводка», commora-console.md §10).
 *
 * Заголовок — `type-section-title` (18/600, h2), ссылка справа — текст цвета
 * действия со стрелкой, цель касания по теме (48 до 1024, 32 с 1024).
 * Карточка белая, скругление 12, тонкая линия, без тени — она не кликабельна
 * целиком (§11: реакция на наведение только у ссылок внутри).
 *
 * ```tsx
 * <SectionCard title="Очередь сборки" link={{ label: "Все задания", to: `${base}/picking` }}>
 *   …
 * </SectionCard>
 * ```
 */

import { ChevronRight } from "lucide-react";
import { useId, type ReactNode } from "react";
import { Link } from "react-router";

import { cn } from "@/lib/utils";

interface Props {
  title: string;
  /** Переход «ко всему списку» справа от заголовка. */
  link?: { label: string; to: string };
  children: ReactNode;
  className?: string;
}

export function SectionCard({ title, link, children, className }: Props) {
  const headingId = useId();
  return (
    <section
      aria-labelledby={headingId}
      className={cn(
        "flex min-w-0 flex-col gap-3 rounded-xl bg-surface-lowest p-4 [box-shadow:inset_0_0_0_1px_var(--border)]",
        className,
      )}
    >
      <div className="flex min-w-0 items-center justify-between gap-2">
        {/* Заголовок переносится, а не режется: на 360 рядом стоит ссылка. */}
        <h2 id={headingId} className="type-section-title min-w-0 text-on-surface">
          {title}
        </h2>
        {link ? (
          <Link
            to={link.to}
            className="type-body inline-flex min-h-[var(--control-compact-h,3rem)] shrink-0 items-center gap-1 rounded-md px-1 font-medium text-primary outline-none hover:underline focus-visible:ring-2 focus-visible:ring-ring"
          >
            {link.label}
            <ChevronRight aria-hidden className="size-4" />
          </Link>
        ) : null}
      </div>
      {children}
    </section>
  );
}
